import { makeAutoObservable } from 'mobx'
import { v4 as uuidv4 } from 'uuid'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type RawTreeNode<T extends { type: any } = { type: any }> = T & {
  treeKey: string
  children?: RawTreeNode[]
  type?: T extends { type: infer U } ? U : never
}

export type FlatTreeNode<U extends RawTreeNode> = Omit<U, 'children'> & {
  parentTreeKey: RawTreeNode['treeKey'] | null
  childrenTreeKeys?: RawTreeNode['treeKey'][]
}

// Ordered Indexed Flat Tree Forest
// 모든 노드가 Map에 평면적으로 저장되면서, 배열로 관리되는 자식 참조를 통해 순서가 보장되는 트리 구조를 표현하는 자료구조
// root가 여러개라서 forest인데, 이름이 너무 길어져서 일단 FlatTree로 명칭
export class FlatTree<U extends RawTreeNode> {
  private _map: Map<U['treeKey'], FlatTreeNode<U>>

  constructor(rawData: U[] | FlatTree<U>) {
    makeAutoObservable(this)
    if (rawData instanceof FlatTree) {
      this._map = new Map(
        Array.from(rawData._map.entries()).map(([key, value]) => [
          key,
          {
            ...value,
            childrenTreeKeys: value.childrenTreeKeys ? [...value.childrenTreeKeys] : undefined,
          },
        ]),
      )
    } else {
      this._map = this._buildTreeFromRaw(rawData)
    }
  }

  static treeNode = {
    fromRawDataToRawTreeNode: <
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      D extends { type: any },
      K extends keyof D & (string | number), // K는 D의 key이면서 string 또는 number
    >(
      data: D,
      treeKeyName?: K extends keyof D ? (D[K] extends string | number ? K : never) : never,
    ): RawTreeNode<D> => {
      return {
        ...data,
        treeKey: treeKeyName ? `${data[treeKeyName]}` : uuidv4(),
      }
    },
    fromRawTreeNodeToRawData: <D extends RawTreeNode>({
      treeKey,
      ...data
    }: D): Omit<D, 'treeKey'> => {
      return data
    },
  }

  private _buildTreeFromRaw = (rawNodes: U[]): Map<U['treeKey'], FlatTreeNode<U>> => {
    const _tree = new Map<U['treeKey'], FlatTreeNode<U>>()

    function traverse(
      { children, ...node }: U,
      parentId: RawTreeNode['treeKey'] | null,
      visited = new Set<U['treeKey']>(),
    ) {
      if (visited.has(node.treeKey)) {
        console.warn('_buildTreeFromRaw 순환 발생', node.treeKey)
        return // 이미 방문한 노드는 재귀 중단
      }

      visited.add(node.treeKey)

      const flatNode: FlatTreeNode<U> = {
        ...node,
        parentTreeKey: parentId,
        childrenTreeKeys: children === undefined ? undefined : [],
      }

      _tree.set(node.treeKey, flatNode)

      if (children) {
        for (const child of children) {
          if (flatNode.childrenTreeKeys) {
            flatNode.childrenTreeKeys.push(child.treeKey)
          } else {
            flatNode.childrenTreeKeys = [child.treeKey]
          }
          traverse(child as U, node.treeKey, visited)
        }
      }
    }

    for (const root of rawNodes) {
      traverse(root, null)
    }

    return _tree
  }

  static makeRawTreeFromFlatTree = <U extends RawTreeNode>(
    flatTree: FlatTree<U>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): U[] | U extends any ? U[] : never => {
    // 루트 노드들을 찾아서 시작
    const rootNodes = flatTree.toRootArr
    const result: U[] = []

    function buildRawNode(flatNode: FlatTreeNode<U>): U {
      // FlatTreeNode에서 parentId와 childrenIds를 제외한 속성들을 추출
      const { parentTreeKey, childrenTreeKeys, ...nodeProps } = flatNode
      if (childrenTreeKeys) {
        // 자식 노드들을 재귀적으로 처리
        const children: RawTreeNode[] = []
        for (const childTreeKey of childrenTreeKeys) {
          const childNode = flatTree.get(childTreeKey)
          if (childNode) {
            children.push(buildRawNode(childNode))
          }
        }
        // 원래 구조로 복원
        return FlatTree.treeNode.fromRawTreeNodeToRawData({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ...(nodeProps as any),
          children: children.length > 0 ? children : [],
        })
      } else {
        return FlatTree.treeNode.fromRawTreeNodeToRawData({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ...(nodeProps as any),
        })
      }
    }

    // 루트 노드부터 시작하여 트리 구조 재구성
    for (const rootNode of rootNodes) {
      result.push(buildRawNode(rootNode))
    }

    return result
  }

  getDepth(treeKey: U['treeKey']) {
    const node = this._map.get(treeKey)
    if (!node) {
      throw new Error('Node not found')
    }
    if (node.parentTreeKey !== null) {
      return 1 + this.getDepth(node.parentTreeKey)
    }
    return 0
  }

  isOnlyChild(treeKey: U['treeKey']): boolean {
    const node = this._map.get(treeKey)

    if (!node) {
      throw new Error('Node not found')
    }

    if (node.parentTreeKey === null) {
      return this.toRootArr.length === 1
    } else {
      const parent = this._map.get(node.parentTreeKey)

      if (!parent) {
        throw new Error('Parent not found')
      }

      const siblings = parent.childrenTreeKeys?.filter((key) => key !== treeKey)

      if (!siblings) {
        throw new Error('Not a valid forest')
      }

      return siblings.length === 0
    }
  }

  get toRootArr() {
    return this.toArr.filter((node) => node.parentTreeKey === null)
  }

  get toLeafArr() {
    return this.toArr.filter((node) => !node.childrenTreeKeys || node.childrenTreeKeys.length === 0)
  }

  get toArr() {
    return [...this._map.values()]
  }

  get size() {
    return this._map.size
  }

  getAncestors = (nodeId: U['treeKey']): FlatTreeNode<U>[] => {
    const ancestors: FlatTreeNode<U>[] = []
    let current = this._map.get(nodeId)
    if (!current) return []

    while (current?.parentTreeKey !== null) {
      const parent = this._map.get(current.parentTreeKey)
      if (!parent) break
      ancestors.unshift(parent) // 루트 → 현재 순서로 쌓음
      current = parent
    }

    return ancestors
  }

  findRootNode = (nodeId: U['treeKey']): FlatTreeNode<U> | undefined => {
    const node = this._map.get(nodeId)
    if (!node) return undefined
    if (node.parentTreeKey === null) return node
    return this.findRootNode(node.parentTreeKey)
  }

  getAllLeavesOfRootNode = (rootNodeId: U['treeKey']): FlatTreeNode<U>[] => {
    const rootNode = this._map.get(rootNodeId)
    if (!rootNode) {
      return []
    }

    const allChildren: FlatTreeNode<U>[] = []
    const visited = new Set<U['treeKey']>()

    const collectChildren = (node: FlatTreeNode<U>) => {
      if (visited.has(node.treeKey)) {
        console.warn('getAllChildrenOfRootNode 순환 발생', node.treeKey)
        return
      }

      visited.add(node.treeKey)

      // 현재 노드가 말단 노드인지 확인
      if (!node.childrenTreeKeys || node.childrenTreeKeys.length === 0) {
        allChildren.push(node)
        return
      }

      // 자식 노드들을 재귀적으로 처리
      for (const childTreeKey of node.childrenTreeKeys) {
        const childNode = this._map.get(childTreeKey)
        if (childNode) {
          collectChildren(childNode)
        }
      }
    }

    collectChildren(rootNode)
    return allChildren
  }

  getAncestorsWithSingleChild = (startNodeId: U['treeKey']): FlatTreeNode<U>[] => {
    const deleted: FlatTreeNode<U>[] = []

    let currentId: U['treeKey'] | null = startNodeId

    while (currentId) {
      const current = this._map.get(currentId)
      if (!current) break

      deleted.push(current)

      const parentId = current.parentTreeKey
      if (!parentId) break

      const parent = this._map.get(parentId)
      if (!parent) break

      const siblings = parent.childrenTreeKeys
      const remaining = siblings?.filter((id) => id !== currentId)

      if (remaining?.length === 0) {
        // 부모도 삭제 대상
        currentId = parentId
      } else {
        break // 형제가 남아있으면 부모는 삭제되지 않음
      }
    }

    return deleted // 삭제될 노드 id 목록 (하위 → 상위 순서)
  }

  deleteTopNode = (treeKey: U['treeKey']) => {
    const node = this._map.get(treeKey)
    if (!node || node.parentTreeKey !== null) return

    // 자식 노드들을 재귀적으로 삭제
    const deleteChildren = (parentKey: U['treeKey']) => {
      const parent = this._map.get(parentKey)
      if (!parent || !parent.childrenTreeKeys) return

      // 자식 노드들을 순회하면서 재귀적으로 삭제
      for (const childKey of parent.childrenTreeKeys) {
        deleteChildren(childKey)
      }
      // 현재 노드 삭제
      this._map.delete(parentKey)
    }

    // 루트 노드와 그 자식들을 모두 삭제
    deleteChildren(treeKey)
  }

  delete = (treeKey: U['treeKey']) => {
    const node = this._map.get(treeKey)
    if (!node || (node.childrenTreeKeys && node.childrenTreeKeys.length > 0)) return

    const deleteRecursively = (currentKey: U['treeKey']) => {
      const currentNode = this._map.get(currentKey)
      if (!currentNode) return

      const parentKey = currentNode.parentTreeKey

      const parent = parentKey !== null ? this._map.get(parentKey) : null
      if (parent) {
        // 부모의 자식 배열에서 현재 노드를 제거
        parent.childrenTreeKeys = parent.childrenTreeKeys?.filter(
          (childTreeKey) => childTreeKey !== currentKey,
        )
        this._map.delete(currentKey)
        this._map.set(parent.treeKey, parent)

        // 부모가 자식이 0명이 되었으면, 부모도 삭제 대상으로 올림
        if (!parent.childrenTreeKeys || parent.childrenTreeKeys.length === 0) {
          deleteRecursively(parent.treeKey)
        }
      } else {
        // 루트 노드도 자식이 0명이면 삭제
        this._map.delete(currentKey)
      }
    }

    deleteRecursively(treeKey)
  }

  has = (id: U['treeKey']) => {
    return this._map.has(id)
  }

  get = (id: U['treeKey']) => {
    return this._map.get(id)
  }

  set = (id: U['treeKey'], node: FlatTreeNode<U>) => {
    this._map.set(id, node)
  }

  // Leaf-only insertion 전략
  // : 아직까지는 sub tree는 변경되지 않는 전략. 필요 시 subtree 조작에 대한 것은 따로 메소드로 구현해서 사용할 것
  addLeafByIndex = ({
    parentTreeKey,
    index,
    node,
  }: {
    parentTreeKey: U['treeKey']
    index: number
    node: U | FlatTreeNode<U>
  }) => {
    const parent = this._map.get(parentTreeKey)
    if (!parent) return

    const flatNode: FlatTreeNode<U> = {
      ...node,
      parentTreeKey,
      childrenTreeKeys: [], // 항상 빈 배열로 초기화
    }

    parent.childrenTreeKeys?.splice(index, 0, node.treeKey)
    this._map.set(node.treeKey, flatNode)
    return flatNode
  }

  replaceLeafByIndex = ({
    parentTreeKey,
    index,
    node,
  }: {
    parentTreeKey: U['treeKey']
    index: number
    node: U
  }) => {
    const parent = this._map.get(parentTreeKey)
    if (!parent || !parent.childrenTreeKeys || parent.childrenTreeKeys.length === 0) return

    const newNode = this.addLeafByIndex({ parentTreeKey, index: index + 1, node })
    this.delete(parent.childrenTreeKeys[index])
    return newNode
  }

  addLeaves = ({ parentTreeKey, nodes }: { parentTreeKey: U['treeKey']; nodes: U[] }) => {
    const parent = this._map.get(parentTreeKey)

    if (!parent) return

    nodes.forEach((node) => {
      const flatNode: FlatTreeNode<U> = {
        ...node,
        parentTreeKey,
        childrenTreeKeys: [],
      }

      if (parent.childrenTreeKeys?.includes(node.treeKey)) {
        console.warn('이미 존재하는 노드', node.treeKey)
        return
      }
      if (parent.childrenTreeKeys) {
        parent.childrenTreeKeys.push(node.treeKey)
      } else {
        parent.childrenTreeKeys = [node.treeKey]
      }
      this._map.set(node.treeKey, flatNode)
    })
  }

  static countLeafNodesInNestedTree = <U extends RawTreeNode>(
    tree: FlatTree<U>,
    node: FlatTreeNode<U>,
    visited: Set<U['treeKey']> = new Set(),
  ): number => {
    if (visited.has(node.treeKey)) {
      console.warn('순환 발생', node.treeKey)
      // 순환 발생 시 0 반환 또는 에러 throw
      return 0
    }
    visited.add(node.treeKey)

    if (!node.childrenTreeKeys || node.childrenTreeKeys.length === 0) {
      return 1
    }

    let count = 0

    for (const childId of node.childrenTreeKeys) {
      const child = tree.get(childId)
      if (!child) continue

      count += FlatTree.countLeafNodesInNestedTree(tree, child, visited)
    }

    return count
  }

  isParentOfLeaf = (id: U['treeKey']) => {
    const node = this._map.get(id)
    if (!node) return false

    return node.childrenTreeKeys?.some((id) => {
      const child = this._map.get(id)
      return child !== undefined && (!child.childrenTreeKeys || child.childrenTreeKeys.length === 0)
    })
  }

  findLeaf = (id: U['treeKey']): FlatTreeNode<U> | undefined => {
    const node = this._map.get(id)
    if (!node) return undefined

    if (!node.childrenTreeKeys || node.childrenTreeKeys.length === 0) {
      return node
    }

    for (const childId of node.childrenTreeKeys || []) {
      const leaf = this.findLeaf(childId)
      if (leaf) return leaf
    }
    return undefined
  }

  getNextNavigableNode = (nodeId: U['treeKey']) => {
    const tree = this._map
    let current = tree.get(nodeId)
    if (!current) return null

    while (current?.parentTreeKey) {
      const parent = tree.get(current.parentTreeKey)
      if (!parent) break

      const siblings = parent.childrenTreeKeys
      if (siblings) {
        const idx = siblings.indexOf(current.treeKey)
        if (siblings.length > 1) {
          if (idx !== -1 && idx + 1 < siblings.length) {
            // 다음 형제 노드가 있음
            return tree.get(siblings[idx + 1]) ?? null
          } else if (idx > 0) {
            // 앞 형제 노드가 있음
            return tree.get(siblings[idx - 1]) ?? null
          } else {
            // 첫 번째 형제이므로 앞 형제가 없음
            return null
          }
        }
      }
      // 형제가 없으면 위로 이동
      current = parent
    }

    // 여기서 current는 루트 노드임
    // 전체 루트 노드 배열에서 다음 루트 노드를 찾아 반환
    const roots = this.toRootArr
    const idx = roots.findIndex((n) => n.treeKey === current?.treeKey)
    if (idx !== -1 && idx + 1 < roots.length) {
      return roots[idx + 1]
    }

    return null // 마지막 루트 노드라면 null
  }
}
